import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["loading", "list", "empty"]
  static values = { clientId: String, clipsUrl: String }

  connect() {
    this.loaded = false
  }

  load() {
    if (this.loaded) return
    this.loaded = true

    this.loadingTarget.classList.remove("hidden")
    this.listTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")

    // DEF-11: the path is rendered from the route helper, not hardcoded (it
    // was pointing at /admin while the route lives under the /sm scope, so
    // this picker 404'd for every client).
    fetch(this.clipsUrlValue, {
      headers: { "Accept": "application/json" }
    })
      .then(response => response.json())
      .then(data => this.renderClips(data))
      .catch(error => {
        console.error("Error loading press clips:", error)
        this.loadingTarget.innerHTML = `<p class="text-sm text-[#E57373]">Failed to load press clips. Please try again.</p>`
      })
  }

  renderClips(clips) {
    this.loadingTarget.classList.add("hidden")

    if (clips.length === 0) {
      this.emptyTarget.classList.remove("hidden")
      return
    }

    this.listTarget.innerHTML = ""
    this.listTarget.classList.remove("hidden")

    clips.forEach(clip => {
      const card = document.createElement("div")
      card.className = "p-4 border border-[#E8E4DF] rounded-lg"

      const storyBadge = clip.has_story_version
        ? `<span class="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-[#9CAF88]/20 text-[#9CAF88]">
             <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
             Story Ready
           </span>`
        : ""

      const thumbnailHtml = clip.image_url
        ? `<div class="w-20 h-20 rounded bg-[#F5F3F0] overflow-hidden flex-shrink-0">
             <img src="${this.escapeAttr(clip.image_url)}" class="w-full h-full object-cover" loading="lazy"
                  onerror="this.parentElement.innerHTML='<div class=\\'w-full h-full flex items-center justify-center text-[#8C8C8C]\\'>No preview</div>'">
           </div>`
        : `<div class="w-20 h-20 rounded bg-[#F5F3F0] flex items-center justify-center flex-shrink-0 text-[#8C8C8C] text-xs">No image</div>`

      const storyThumbHtml = clip.has_story_version && clip.story_image_url
        ? `<div class="w-12 h-20 rounded bg-[#F5F3F0] overflow-hidden flex-shrink-0 border-2 border-[#9CAF88]">
             <img src="${this.escapeAttr(clip.story_image_url)}" class="w-full h-full object-cover" loading="lazy">
           </div>`
        : ""

      // Build action buttons
      let buttons = ""
      if (clip.image_url) {
        buttons += `<a href="${this.escapeAttr(clip.image_url)}" target="_blank" class="inline-flex items-center px-3 py-1.5 bg-[#C4A484] text-white text-xs font-medium rounded-lg hover:bg-[#B39474] transition-colors">
          <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
          Download Clip</a>`
      }

      if (clip.has_story_version) {
        buttons += ` <a href="${this.escapeAttr(clip.edit_story_url)}" class="inline-flex items-center px-3 py-1.5 border border-[#C4A484] text-[#C4A484] text-xs font-medium rounded-lg hover:bg-[#C4A484]/10 transition-colors">
          <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          Edit Story</a>`
        if (clip.download_story_url) {
          buttons += ` <a href="${this.escapeAttr(clip.download_story_url)}" class="inline-flex items-center px-3 py-1.5 border border-[#E8E4DF] text-[#8C8C8C] text-xs font-medium rounded-lg hover:bg-[#F5F3F0] transition-colors">
            <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Download Story</a>`
        }
        buttons += ` <a href="${this.escapeAttr(clip.delete_story_url)}" data-turbo-method="delete" data-turbo-confirm="Delete this story version?" class="inline-flex items-center px-3 py-1.5 border border-[#E57373]/30 text-[#E57373] text-xs font-medium rounded-lg hover:bg-[#E57373]/5 transition-colors">Delete Story</a>`
      } else {
        buttons += ` <a href="${this.escapeAttr(clip.new_story_url)}" class="inline-flex items-center px-3 py-1.5 border border-[#C4A484] text-[#C4A484] text-xs font-medium rounded-lg hover:bg-[#C4A484]/10 transition-colors">
          <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
          Story Version</a>`
      }

      if (clip.article_url) {
        buttons += ` <a href="${this.escapeAttr(clip.article_url)}" target="_blank" class="inline-flex items-center px-3 py-1.5 border border-[#E8E4DF] text-[#8C8C8C] text-xs font-medium rounded-lg hover:bg-[#F5F3F0] transition-colors">
          <svg class="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
          View Article</a>`
      }

      if (clip.dpk_url) {
        buttons += ` <a href="${this.escapeAttr(clip.dpk_url)}" target="_blank" class="inline-flex items-center px-3 py-1.5 border border-[#E8E4DF] text-[#8C8C8C] text-xs font-medium rounded-lg hover:bg-[#F5F3F0] transition-colors">Open DPK</a>`
      }

      card.innerHTML = `
        <div class="flex items-start space-x-4">
          ${thumbnailHtml}
          ${storyThumbHtml}
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-2 mb-1">
              <span class="text-sm font-medium text-[#2D2D2D]">${this.escapeHtml(clip.outlet_name || "Press Clip")}</span>
              <span class="text-xs text-[#8C8C8C]">${this.escapeHtml(clip.created_at)}</span>
              ${storyBadge}
            </div>
            ${clip.created_by ? `<p class="text-xs text-[#8C8C8C] mb-2">Created by: ${this.escapeHtml(clip.created_by)}</p>` : ""}
            <div class="flex flex-wrap items-center gap-2">${buttons}</div>
          </div>
        </div>
      `

      this.listTarget.appendChild(card)
    })
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    if (!text) return ""
    return text.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
